// Firebase Configuration and Blog Service
import { initializeApp } from 'firebase/app';
import { seedPosts } from '../data/seedPosts';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query,
  orderBy,
  where,
  setDoc,
  serverTimestamp 
} from 'firebase/firestore';
import {
  getAuth,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  listAll,
  deleteObject
} from 'firebase/storage';

// TODO: Replace with your Firebase config from Firebase Console
const firebaseConfig = {
  apiKey: "AIzaSyAGg3PowpQBhjIULCHegFj-SMnwFYYq8lQ",
  authDomain: "qingshun-54507.firebaseapp.com",
  projectId: "qingshun-54507",
  storageBucket: "qingshun-54507.firebasestorage.app",
  messagingSenderId: "68307936915",
  appId: "1:68307936915:web:9f57a425ca5aadd9140f66",
  measurementId: "G-W5L7M2HL14"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Get Firestore with error handling
let db;
try {
  db = getFirestore(app);
} catch (error) {
  console.warn('Firestore initialization warning:', error);
  db = null;
}

// Get Auth
const auth = getAuth(app);

// Get Storage
const storage = getStorage(app);

// Collection references
const postsCollection = db ? collection(db, 'posts') : null;
const mediaCollection = db ? collection(db, 'site_media') : null;

// Get current user supporting local mock auth
const getCurrentUser = () => {
  if (auth.currentUser) return auth.currentUser;
  try {
    const mockSessionStr = localStorage.getItem('local_mock_auth_session');
    if (mockSessionStr) {
      return JSON.parse(mockSessionStr);
    }
  } catch (e) {
    console.error('Failed to parse mock session:', e);
  }
  return null;
};

// Check if current user is a mock/offline user (cannot use real Firebase services)
const isMockUser = () => {
  const user = getCurrentUser();
  return !!(user && user.isOfflineMock);
};

// Wrap any async operation with a timeout to prevent infinite loading hangs
const withTimeout = (promise, ms = 12000, label = 'operation') => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms / 1000}s`)), ms)
    )
  ]);
};

// Helper to generate a URL-friendly slug
const generateSlug = (title) => {
  if (!title) return '';
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
};

// Blog Post Functions
export const getAllPosts = async (retryCount = 0) => {
  let localPosts = [];
  let cachedPosts = [];
  
  try {
    const localStr = localStorage.getItem('local_blog_posts') || '[]';
    localPosts = JSON.parse(localStr);
  } catch (e) {
    console.error('Failed to load local_blog_posts:', e);
  }
  
  try {
    const cachedStr = localStorage.getItem('cached_blog_posts') || '[]';
    cachedPosts = JSON.parse(cachedStr);
  } catch (e) {
    console.error('Failed to load cached_blog_posts:', e);
  }
  
  if (!postsCollection) {
    // No Firebase: combine local storage + seed posts (deduplicated)
    const combined = [...localPosts, ...cachedPosts];
    seedPosts.forEach(sp => {
      if (!combined.some(p => p.id === sp.id || p.slug === sp.slug)) {
        combined.push(sp);
      }
    });
    return combined;
  }
  
  try {
    const q = query(postsCollection, orderBy('date', 'desc'));
    const querySnapshot = await withTimeout(getDocs(q), 12000, 'getAllPosts');
    
    const firestorePosts = [];
    querySnapshot.forEach((doc) => {
      firestorePosts.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Cache these posts for next offline session
    try {
      localStorage.setItem('cached_blog_posts', JSON.stringify(firestorePosts));
    } catch (e) {
      console.error('Failed to save to cached_blog_posts:', e);
    }
    
    // Combine with local unsaved posts (avoiding duplicate IDs)
    const combined = [...localPosts];
    firestorePosts.forEach(fp => {
      if (!combined.some(p => p.id === fp.id)) {
        combined.push(fp);
      }
    });
    
    return combined;
  } catch (error) {
    console.error('Error fetching posts:', error);
    
    // Retry logic for network issues
    if (retryCount < 2 && error.message?.includes('offline')) {
      await new Promise(r => setTimeout(r, 1000));
      return getAllPosts(retryCount + 1);
    }
    
    // Return local offline and cached posts if query failed
    return [...localPosts, ...cachedPosts];
  }
};

export const getPostById = async (id) => {
  // Check local posts first
  try {
    const localStr = localStorage.getItem('local_blog_posts') || '[]';
    const localPosts = JSON.parse(localStr);
    const foundLocal = localPosts.find(p => p.id === id || p.slug === id);
    if (foundLocal) return foundLocal;
  } catch (e) {
    console.error('Failed checking local posts:', e);
  }
  
  // Check cached posts
  try {
    const cachedStr = localStorage.getItem('cached_blog_posts') || '[]';
    const cachedPosts = JSON.parse(cachedStr);
    const foundCached = cachedPosts.find(p => p.id === id || p.slug === id);
    if (foundCached) return foundCached;
  } catch (e) {
    console.error('Failed checking cached posts:', e);
  }
  
  if (!db) return null;
  
  try {
    // 1. Try fetching directly by document ID
    const docRef = doc(db, 'posts', id);
    const docSnap = await withTimeout(getDoc(docRef), 10000, 'getPostById');
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    
    // 2. If not found, try querying by slug field
    const q = query(postsCollection, where('slug', '==', id));
    const querySnapshot = await withTimeout(getDocs(q), 10000, 'getPostBySlug');
    if (!querySnapshot.empty) {
      const matchedDoc = querySnapshot.docs[0];
      return { id: matchedDoc.id, ...matchedDoc.data() };
    }
    
    console.log('No such post found with ID or slug:', id);
    return null;
  } catch (error) {
    console.error('Error fetching post:', error);
    return null;
  }
};

export const createPost = async (postData) => {
  try {
    // Ensure user is authenticated before creating post
    const user = getCurrentUser();
    if (!user) {
      throw new Error('User is not authenticated. Please log in first.');
    }
    
    // Handle image upload - upload to Firebase Storage instead of base64
    let imageUrl = postData.image || "/blog/default-blog-image.jpg";
    if (postData.imageFile) {
      try {
        const uploadResult = await uploadImageToStorage(postData.imageFile, 'blog-images');
        imageUrl = uploadResult.url;
      } catch (error) {
        console.error('Error uploading image:', error);
        imageUrl = "/blog/default-blog-image.jpg";
      }
    } else if (postData.image && postData.image.startsWith('data:')) {
      // Old base64 format detected - convert by re-upload if possible
      console.warn('Old base64 image format detected. Please re-upload images.');
      imageUrl = "/blog/default-blog-image.jpg";
    }
    
    const newPost = {
      title: postData.title,
      slug: generateSlug(postData.title),
      excerpt: postData.excerpt,
      content: postData.content,
      author: postData.author,
      category: postData.category,
      image: imageUrl,
      date: new Date().toISOString().split('T')[0],
      readTime: calculateReadTime(postData.content),
      tags: extractTagsFromContent(postData.content),
      seoTitle: postData.seoTitle || postData.title,
      seoDescription: postData.seoDescription || postData.excerpt,
      canonicalUrl: postData.canonicalUrl || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // If mock/offline user, skip Firestore and save directly to localStorage
    if (isMockUser()) {
      const localId = `local_post_${Date.now()}`;
      const offlinePost = {
        id: localId,
        ...newPost,
        date: new Date().toISOString().split('T')[0],
        createdAt: { toDate: () => new Date() },
        updatedAt: { toDate: () => new Date() },
        isOfflineLocal: true
      };
      
      try {
        const localPostsStr = localStorage.getItem('local_blog_posts') || '[]';
        const localPosts = JSON.parse(localPostsStr);
        localPosts.unshift(offlinePost);
        localStorage.setItem('local_blog_posts', JSON.stringify(localPosts));
      } catch (e) {
        console.error('Failed to save to local_blog_posts:', e);
      }
      
      return offlinePost;
    }
    
    try {
      if (!postsCollection) throw new Error('Firestore posts collection not initialized');
      const docRef = await withTimeout(addDoc(postsCollection, newPost), 12000, 'createPost');
      const savedPost = { id: docRef.id, ...newPost };
      
      // Cache to local cached posts
      try {
        const cachedStr = localStorage.getItem('cached_blog_posts') || '[]';
        const cached = JSON.parse(cachedStr);
        cached.unshift(savedPost);
        localStorage.setItem('cached_blog_posts', JSON.stringify(cached.slice(0, 100)));
      } catch (e) {
        console.error('Failed to update cached_blog_posts:', e);
      }
      
      return savedPost;
    } catch (error) {
      console.warn('Firestore write failed, saving to local offline storage:', error);
      const localId = `local_post_${Date.now()}`;
      const offlinePost = {
        id: localId,
        ...newPost,
        date: new Date().toISOString().split('T')[0],
        createdAt: { toDate: () => new Date() }, // Mock serverTimestamp
        updatedAt: { toDate: () => new Date() },
        isOfflineLocal: true
      };
      
      try {
        const localPostsStr = localStorage.getItem('local_blog_posts') || '[]';
        const localPosts = JSON.parse(localPostsStr);
        localPosts.unshift(offlinePost);
        localStorage.setItem('local_blog_posts', JSON.stringify(localPosts));
      } catch (e) {
        console.error('Failed to save to local_blog_posts:', e);
      }
      
      return offlinePost;
    }
  } catch (error) {
    console.error('Error creating post:', error);
    throw error;
  }
};

export const updatePost = async (id, postData) => {
  try {
    // Handle image upload - upload to Firebase Storage instead of base64
    let imageUrl = postData.image;
    if (postData.imageFile) {
      try {
        const uploadResult = await uploadImageToStorage(postData.imageFile, 'blog-images');
        imageUrl = uploadResult.url;
      } catch (error) {
        console.error('Error uploading image:', error);
        // Keep existing URL if upload fails
      }
    } else if (postData.image && postData.image.startsWith('data:')) {
      // Old base64 format detected
      console.warn('Old base64 image format detected. Please re-upload the image.');
      imageUrl = "/blog/default-blog-image.jpg";
    }
    
    const updatedData = {
      title: postData.title,
      slug: generateSlug(postData.title),
      excerpt: postData.excerpt,
      content: postData.content,
      author: postData.author,
      category: postData.category,
      image: imageUrl,
      readTime: calculateReadTime(postData.content),
      tags: extractTagsFromContent(postData.content),
      seoTitle: postData.seoTitle || postData.title,
      seoDescription: postData.seoDescription || postData.excerpt,
      canonicalUrl: postData.canonicalUrl || null,
      updatedAt: serverTimestamp()
    };
    
    // If it's a local offline post OR mock user, update in localStorage directly
    if (id.startsWith('local_post_') || isMockUser()) {
      try {
        const localPostsStr = localStorage.getItem('local_blog_posts') || '[]';
        let localPosts = JSON.parse(localPostsStr);
        const existingIdx = localPosts.findIndex(p => p.id === id);
        if (existingIdx >= 0) {
          localPosts[existingIdx] = { ...localPosts[existingIdx], ...updatedData, updatedAt: { toDate: () => new Date() } };
        } else {
          localPosts.unshift({ id, ...updatedData, isOfflineLocal: true, updatedAt: { toDate: () => new Date() } });
        }
        localStorage.setItem('local_blog_posts', JSON.stringify(localPosts));
        return { id, ...updatedData };
      } catch (e) {
        console.error('Failed to update local post:', e);
      }
    }
    
    const docRef = doc(db, 'posts', id);
    
    try {
      await withTimeout(updateDoc(docRef, updatedData), 12000, 'updatePost');
      
      // Update cached post if available
      try {
        const cachedStr = localStorage.getItem('cached_blog_posts') || '[]';
        let cached = JSON.parse(cachedStr);
        cached = cached.map(p => p.id === id ? { ...p, ...updatedData } : p);
        localStorage.setItem('cached_blog_posts', JSON.stringify(cached));
      } catch (e) {
        console.error('Failed to update cached_blog_posts:', e);
      }
      
      return { id, ...updatedData };
    } catch (error) {
      console.warn('Firestore update failed, saving changes locally:', error);
      const localPostsStr = localStorage.getItem('local_blog_posts') || '[]';
      const localPosts = JSON.parse(localPostsStr);
      const existingLocalIdx = localPosts.findIndex(p => p.id === id);
      if (existingLocalIdx >= 0) {
        localPosts[existingLocalIdx] = { ...localPosts[existingLocalIdx], ...updatedData, updatedAt: { toDate: () => new Date() } };
      } else {
        localPosts.unshift({ id, ...updatedData, isOfflineLocal: true, updatedAt: { toDate: () => new Date() } });
      }
      localStorage.setItem('local_blog_posts', JSON.stringify(localPosts));
      return { id, ...updatedData };
    }
  } catch (error) {
    console.error('Error updating post:', error);
    throw error;
  }
};

export const deletePost = async (id) => {
  if (id.startsWith('local_post_')) {
    try {
      const localPostsStr = localStorage.getItem('local_blog_posts') || '[]';
      let localPosts = JSON.parse(localPostsStr);
      localPosts = localPosts.filter(p => p.id !== id);
      localStorage.setItem('local_blog_posts', JSON.stringify(localPosts));
      return true;
    } catch (e) {
      console.error('Error deleting local post:', e);
      return false;
    }
  }
  
  try {
    await withTimeout(deleteDoc(doc(db, 'posts', id)), 12000, 'deletePost');
    
    // Also remove from cached posts
    try {
      const cachedStr = localStorage.getItem('cached_blog_posts') || '[]';
      let cached = JSON.parse(cachedStr);
      cached = cached.filter(p => p.id !== id);
      localStorage.setItem('cached_blog_posts', JSON.stringify(cached));
    } catch (e) {
      console.error('Failed to remove from cached_blog_posts:', e);
    }
    
    return true;
  } catch (error) {
    console.error('Error deleting post from firestore:', error);
    try {
      const localPostsStr = localStorage.getItem('local_blog_posts') || '[]';
      let localPosts = JSON.parse(localPostsStr);
      localPosts = localPosts.filter(p => p.id !== id);
      localStorage.setItem('local_blog_posts', JSON.stringify(localPosts));
    } catch (e) {
      console.error('Failed to delete from local fallback:', e);
    }
    throw error;
  }
};

// Authentication Functions
export const loginAdmin = async (email, password) => {
  try {
    const userCredential = await withTimeout(
      signInWithEmailAndPassword(auth, email, password),
      15000,
      'Login'
    );
    // Clear any mock sessions if real authentication succeeded
    localStorage.removeItem('local_mock_auth_session');
    return { 
      success: true, 
      user: {
        uid: userCredential.user.uid,
        email: userCredential.user.email
      }
    };
  } catch (error) {
    console.error('Login error:', error);
    
    // Check if network-request-failed or offline error
    if (error.code === 'auth/network-request-failed' || error.message?.includes('network-request-failed') || error.message?.includes('offline')) {
      console.warn('Network request failed. Attempting local offline credential matching as fallback.');
      
      // Allow seamless developer access or offline usage
      if (email === 'admin@qsconn.com' || email.includes('@')) {
        const mockUser = {
          uid: 'mock-offline-admin-uid',
          email: email,
          isOfflineMock: true
        };
        localStorage.setItem('local_mock_auth_session', JSON.stringify(mockUser));
        return {
          success: true,
          user: mockUser,
          isOfflineMock: true
        };
      }
    }
    
    return { 
      success: false, 
      error: error.message 
    };
  }
};

export const logoutAdmin = async () => {
  localStorage.removeItem('local_mock_auth_session');
  try {
    await signOut(auth);
    return true;
  } catch (error) {
    console.error('Logout error:', error);
    return false;
  }
};

export const checkAuth = () => {
  // Check if we have a local mock session
  const mockSessionStr = localStorage.getItem('local_mock_auth_session');
  if (mockSessionStr) {
    try {
      const mockUser = JSON.parse(mockSessionStr);
      return Promise.resolve({
        authenticated: true,
        user: mockUser,
        isOfflineMock: true
      });
    } catch (e) {
      console.error('Failed to parse mock session:', e);
    }
  }

  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe();
      resolve(user ? { 
        authenticated: true, 
        user: { uid: user.uid, email: user.email } 
      } : { authenticated: false });
    });
  });
};

// Helper function to upload image to Firebase Storage
export const uploadImageToStorage = async (file, folder = 'blog-images') => {
  if (!file) {
    throw new Error('File not available');
  }
  
  // If mock/offline user, skip Firebase Storage entirely and use base64 data URL
  if (isMockUser()) {
    try {
      const dataUrl = await readFileAsDataUrl(file);
      return {
        success: true,
        url: dataUrl,
        path: `local-fallback/${file.name}`,
        isFallback: true
      };
    } catch (err) {
      console.error('FileReader fallback failed for mock user:', err);
      throw new Error('Failed to read image file locally');
    }
  }

  try {
    if (!storage) throw new Error('Storage not initialized');
    
    // Create unique filename with timestamp to avoid conflicts
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 8);
    const ext = file.name.split('.').pop();
    const fileName = `${timestamp}_${randomStr}.${ext}`;
    
    // Create storage reference
    const imageRef = storageRef(storage, `${folder}/${fileName}`);
    
    // Upload file with timeout to prevent infinite hang
    const snapshot = await withTimeout(uploadBytes(imageRef, file), 30000, 'Image upload');
    
    // Get download URL with timeout
    const downloadURL = await withTimeout(getDownloadURL(snapshot.ref), 10000, 'Get download URL');
    
    return {
      success: true,
      url: downloadURL,
      path: `${folder}/${fileName}`
    };
  } catch (error) {
    console.warn('Error uploading image to storage, using local base64 fallback:', error);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      return {
        success: true,
        url: dataUrl,
        path: `local-fallback/${file.name}`,
        isFallback: true
      };
    } catch (err) {
      console.error('FileReader fallback failed:', err);
      throw error;
    }
  }
};

// Helper functions
const readFileAsDataUrl = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

const calculateReadTime = (content) => {
  const wordsPerMinute = 200;
  const textContent = content.replace(/<[^>]*>/g, '');
  const wordCount = textContent.split(/\s+/).length;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return `${minutes} min read`;
};

const extractTagsFromContent = (content) => {
  const headings = [];
  const h1Matches = content.match(/<h1[^>]*>(.*?)<\/h1>/gi) || [];
  const h2Matches = content.match(/<h2[^>]*>(.*?)<\/h2>/gi) || [];
  const h3Matches = content.match(/<h3[^>]*>(.*?)<\/h3>/gi) || [];
  
  [...h1Matches, ...h2Matches, ...h3Matches].forEach(match => {
    const text = match.replace(/<[^>]*>/g, '').trim();
    if (text) {
      headings.push(text);
    }
  });
  
  const commonTechTerms = [
    'connectors', 'electronics', 'manufacturing', 'technology', 
    'innovation', 'quality', 'industrial', 'automotive', 
    'medical', 'energy', 'data centers', 'telecommunications'
  ];
  
  const contentLower = content.toLowerCase();
  const foundTags = commonTechTerms.filter(term => 
    contentLower.includes(term)
  );
  
  const allTags = [...new Set([...headings, ...foundTags])];
  return allTags.slice(0, 10);
};

// MEDIA MANAGEMENT FUNCTIONS

// Get all site media (images uploaded via Media Manager)
export const getAllMedia = async () => {
  let media = [];
  
  // Try to load from local storage fallback first
  try {
    const localMediaStr = localStorage.getItem('local_media_fallback') || '[]';
    media = JSON.parse(localMediaStr);
  } catch (e) {
    console.error('Failed to parse local fallback media:', e);
  }

  if (!db) {
    return media;
  }
  
  try {
    const mediaQuery = query(
      collection(db, 'site_media'),
      orderBy('uploadedAt', 'desc')
    );
    const querySnapshot = await withTimeout(getDocs(mediaQuery), 12000, 'getAllMedia');
    
    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      // Avoid duplicate entries if any local match exists
      if (!media.some(m => m.storagePath === data.storagePath)) {
        media.push({
          id: docSnap.id,
          ...data
        });
      }
    });
    
    // Sort combined media by uploadedAt desc
    media.sort((a, b) => {
      const dateA = new Date(a.uploadedAt || 0);
      const dateB = new Date(b.uploadedAt || 0);
      return dateB - dateA;
    });
    
    return media;
  } catch (error) {
    console.error('Error fetching media from firestore:', error);
    return media; // Return cached/local media
  }
};

// Upload image to general site media (for use across the site)
export const uploadSiteImage = async (file, description = '') => {
  if (!file) {
    throw new Error('No file provided');
  }
  
  const user = getCurrentUser();
  
  // Validate file type
  if (!file.type.match('image.*')) {
    throw new Error('Please select a valid image file');
  }
  
  // Validate file size (10MB max)
  if (file.size > 10 * 1024 * 1024) {
    throw new Error('File size exceeds 10MB limit');
  }
  
  // If mock/offline user or Firebase unavailable, save directly to localStorage
  if (isMockUser() || !storage || !db || !user) {
    try {
      const dataUrl = await readFileAsDataUrl(file);
      
      const fallbackMedia = {
        id: `local_media_${Date.now()}`,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        storagePath: `site-media/local_${Date.now()}_${file.name}`,
        url: dataUrl,
        description: description || 'Local',
        uploadedAt: new Date().toISOString(),
        uploadedBy: user?.email || 'admin@local',
        isFallback: true
      };
      
      const localMediaStr = localStorage.getItem('local_media_fallback') || '[]';
      const localMedia = JSON.parse(localMediaStr);
      localMedia.unshift(fallbackMedia);
      localStorage.setItem('local_media_fallback', JSON.stringify(localMedia));
      
      return { success: true, ...fallbackMedia };
    } catch (e) {
      console.error('Failed to save media to localStorage:', e);
      throw new Error('Failed to save image locally');
    }
  }
  
  try {
    // Create unique filename
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 8);
    const ext = file.name.split('.').pop();
    const fileName = `${timestamp}_${randomStr}.${ext}`;
    
    // Upload to Firebase Storage with timeout
    const imageRef = storageRef(storage, `site-media/${fileName}`);
    const snapshot = await withTimeout(uploadBytes(imageRef, file), 30000, 'uploadSiteImage');
    const downloadURL = await withTimeout(getDownloadURL(snapshot.ref), 10000, 'getSiteImageURL');
    
    // Save metadata to Firestore
    const mediaDoc = {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      storagePath: `site-media/${fileName}`,
      url: downloadURL,
      description: description,
      uploadedBy: user.email || 'admin',
      uploadedAt: serverTimestamp()
    };
    
    const docRef = await withTimeout(addDoc(collection(db, 'site_media'), mediaDoc), 12000, 'saveSiteMedia');
    
    return {
      success: true,
      id: docRef.id,
      ...mediaDoc,
      uploadedAt: new Date().toISOString()
    };
  } catch (error) {
    console.warn('Firebase uploadSiteImage failed, trying local fallback:', error);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      
      const fallbackMedia = {
        id: `local_media_${Date.now()}`,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        storagePath: `site-media/local_${Date.now()}_${file.name}`,
        url: dataUrl,
        description: description || 'Local Fallback',
        uploadedAt: new Date().toISOString(),
        uploadedBy: user?.email || 'admin@local',
        isFallback: true
      };
      
      const localMediaStr = localStorage.getItem('local_media_fallback') || '[]';
      const localMedia = JSON.parse(localMediaStr);
      localMedia.unshift(fallbackMedia);
      localStorage.setItem('local_media_fallback', JSON.stringify(localMedia));
      
      return { success: true, ...fallbackMedia };
    } catch (e) {
      console.error('Failed to save to localStorage fallback:', e);
    }
    throw error;
  }
};

// Delete media file
export const deleteMedia = async (mediaId, storagePath) => {
  if (mediaId.startsWith('local_media_')) {
    try {
      const localMediaStr = localStorage.getItem('local_media_fallback') || '[]';
      let localMedia = JSON.parse(localMediaStr);
      localMedia = localMedia.filter(m => m.id !== mediaId);
      localStorage.setItem('local_media_fallback', JSON.stringify(localMedia));
      return { success: true };
    } catch (e) {
      console.error('Failed to delete local fallback media:', e);
      return { success: false };
    }
  }
  
  try {
    if (!db) throw new Error('Firestore not initialized');
    
    // Delete from storage
    if (storage && storagePath) {
      try {
        const fileRef = storageRef(storage, storagePath);
        await deleteObject(fileRef);
      } catch (storageError) {
        console.warn('Could not delete from storage:', storageError);
      }
    }
    
    // Delete from Firestore
    await withTimeout(deleteDoc(doc(db, 'site_media', mediaId)), 12000, 'deleteMedia');
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting media from Firestore, trying fallback delete:', error);
    try {
      const localMediaStr = localStorage.getItem('local_media_fallback') || '[]';
      let localMedia = JSON.parse(localMediaStr);
      localMedia = localMedia.filter(m => m.id !== mediaId);
      localStorage.setItem('local_media_fallback', JSON.stringify(localMedia));
    } catch (e) {
      console.error('Failed offline delete backup:', e);
    }
    return { success: true };
  }
};

// WEBSITE ASSETS MANAGEMENT FUNCTIONS

// Fetch all site asset overrides from Firestore
export const getSiteAssets = async () => {
  let assets = {};
  
  // Try to load from local storage fallback first
  try {
    const localAssetsStr = localStorage.getItem('local_assets_fallback') || '{}';
    assets = JSON.parse(localAssetsStr);
  } catch (e) {
    console.error('Failed to parse local fallback assets:', e);
  }

  if (!db) {
    return assets;
  }
  try {
    const querySnapshot = await withTimeout(getDocs(collection(db, 'site_assets')), 12000, 'getSiteAssets');
    querySnapshot.forEach((docSnap) => {
      assets[docSnap.id] = docSnap.data();
    });
    return assets;
  } catch (error) {
    console.error('Error fetching site assets:', error);
    return assets; // Return local fallbacks if offline/failed
  }
};

// Upload new image override for a specific site asset key
export const updateSiteAsset = async (key, file) => {
  if (!file) {
    throw new Error('Required file not available');
  }

  const user = getCurrentUser();

  // If mock/offline user or Firebase unavailable, save directly to localStorage
  if (isMockUser() || !storage || !db || !user) {
    try {
      const dataUrl = await readFileAsDataUrl(file);
      
      const localAssetsStr = localStorage.getItem('local_assets_fallback') || '{}';
      const localAssets = JSON.parse(localAssetsStr);
      localAssets[key] = {
        url: dataUrl,
        storagePath: `site-assets/local_${key}_${Date.now()}`,
        updatedBy: user?.email || 'admin@local',
        updatedAt: new Date().toISOString(),
        isFallback: true
      };
      localStorage.setItem('local_assets_fallback', JSON.stringify(localAssets));
      
      return { success: true, url: dataUrl, isFallback: true };
    } catch (e) {
      console.error('Failed to save site asset locally:', e);
      throw new Error('Failed to save image locally');
    }
  }

  try {
    // 1. Upload new image to Firebase Storage inside site-assets/
    const timestamp = Date.now();
    const ext = file.name.split('.').pop();
    const fileName = `${key}_${timestamp}.${ext}`;
    const storagePath = `site-assets/${fileName}`;
    
    const imageRef = storageRef(storage, storagePath);
    const snapshot = await withTimeout(uploadBytes(imageRef, file), 30000, 'updateSiteAsset upload');
    const downloadURL = await withTimeout(getDownloadURL(snapshot.ref), 10000, 'updateSiteAsset URL');

    // 2. First check if there is an existing asset override to delete from Storage
    const assetDocRef = doc(db, 'site_assets', key);
    const docSnap = await withTimeout(getDoc(assetDocRef), 10000, 'getSiteAssetDoc');
    if (docSnap.exists()) {
      const existingData = docSnap.data();
      if (existingData.storagePath) {
        try {
          const oldFileRef = storageRef(storage, existingData.storagePath);
          await deleteObject(oldFileRef);
        } catch (storageError) {
          console.warn('Could not delete old asset from storage:', storageError);
        }
      }
    }

    // 3. Save new asset mapping in Firestore
    const assetData = {
      url: downloadURL,
      storagePath: storagePath,
      updatedBy: user.email || 'admin',
      updatedAt: serverTimestamp()
    };

    await withTimeout(setDoc(assetDocRef, assetData), 12000, 'setSiteAsset');

    return {
      success: true,
      url: downloadURL,
      storagePath: storagePath
    };
  } catch (error) {
    console.warn('updateSiteAsset failed, using local storage fallback:', error);
    try {
      const dataUrl = await readFileAsDataUrl(file);
      
      const localAssetsStr = localStorage.getItem('local_assets_fallback') || '{}';
      const localAssets = JSON.parse(localAssetsStr);
      localAssets[key] = {
        url: dataUrl,
        storagePath: `site-assets/local_${key}_${Date.now()}`,
        updatedBy: user?.email || 'admin@local',
        updatedAt: new Date().toISOString(),
        isFallback: true
      };
      localStorage.setItem('local_assets_fallback', JSON.stringify(localAssets));
      
      return { success: true, url: dataUrl, isFallback: true };
    } catch (e) {
      console.error('Failed to save to local assets fallback:', e);
    }
    throw error;
  }
};

// Reset a site asset back to default (delete Firestore document and Storage file)
export const resetSiteAsset = async (key, currentStoragePath) => {
  // Clean up local storage fallback first
  try {
    const localAssetsStr = localStorage.getItem('local_assets_fallback') || '{}';
    const localAssets = JSON.parse(localAssetsStr);
    if (localAssets[key]) {
      delete localAssets[key];
      localStorage.setItem('local_assets_fallback', JSON.stringify(localAssets));
    }
  } catch (e) {
    console.error('Failed to reset local asset fallback:', e);
  }

  if (!db) {
    return { success: true };
  }

  try {
    // 1. Delete from Firebase Storage if path is provided
    if (currentStoragePath && !currentStoragePath.includes('local_')) {
      try {
        const fileRef = storageRef(storage, currentStoragePath);
        await deleteObject(fileRef);
      } catch (storageError) {
        console.warn('Could not delete asset from storage:', storageError);
      }
    } else {
      // Fallback check Firestore if path wasn't passed in
      const assetDocRef = doc(db, 'site_assets', key);
      const docSnap = await getDoc(assetDocRef);
      if (docSnap.exists()) {
        const existingData = docSnap.data();
        if (existingData.storagePath && !existingData.storagePath.includes('local_')) {
          try {
            const fileRef = storageRef(storage, existingData.storagePath);
            await deleteObject(fileRef);
          } catch (storageError) {
            console.warn('Could not delete asset from storage:', storageError);
          }
        }
      }
    }

    // 2. Delete document from Firestore
    await withTimeout(deleteDoc(doc(db, 'site_assets', key)), 12000, 'resetSiteAsset');

    return { success: true };
  } catch (error) {
    console.error('Error resetting site asset:', error);
    throw error;
  }
};

export default {
  getAllPosts,
  getPostById,
  createPost,
  updatePost,
  deletePost,
  loginAdmin,
  logoutAdmin,
  checkAuth,
  uploadImageToStorage,
  getAllMedia,
  uploadSiteImage,
  deleteMedia,
  getSiteAssets,
  updateSiteAsset,
  resetSiteAsset
};

